<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";

import { useArticleInject } from "@/composables";
import { DictionariesHelper, NgCompileHelper } from "@/helpers";
import { useArticleFeedbackModalStore, useDictionariesStore } from "@/stores";
import type { Dictionary } from "@/types";

const props = defineProps<{
  methodId: string;
  html: string;
  index: number;
}>();

const { openFeedbackModal } = useArticleFeedbackModalStore();
const { injectFeedbackButton } = useArticleInject("le-ng-article-classic");

const dictionariesStore = useDictionariesStore();

const article = ref<HTMLElement>();

const dictionary = computed<Dictionary | undefined>(() => dictionariesStore.getDictionaryByMethodId(props.methodId));
const isCompanyMethod = computed<boolean>(() => {
  if (!dictionary.value) {
    return false;
  }

  return DictionariesHelper.isCompanyDictionary(dictionary.value);
});

const decorateArticleHeader = (header: Element): void => {
  injectFeedbackButton(header, () => {
    if (!dictionary.value) {
      return;
    }

    openFeedbackModal(props.html, dictionary.value, props.methodId);
  });
};

onMounted(() => {
  watch(
    () => props.html,
    () => {
      if (!article.value) {
        return;
      }

      const headerIndex = article.value.children[0].classList.contains("article-prefix") ? 1 : 0;

      if (!isCompanyMethod.value) {
        decorateArticleHeader(article.value.children[headerIndex]);
      }

      NgCompileHelper.compileNgTemplate(article.value, props.index === 0);
    },
    { immediate: true }
  );
});
</script>

<template>
  <article v-bind="$attrs" ref="article" v-html="props.html"></article>
</template>

<style lang="less" scoped>
.le-ng-article-classic {
  &::v-deep(.article-headline) {
    position: relative;
  }

  ::v-deep(&__feedback) {
    position: absolute;
    top: 18px;
    right: 16px;
  }
}
</style>
